import { useTranslation } from 'react-i18next'

import { HtmlContent } from '@/components/UI/HtmlContent'
import type { SpotStampData } from '@/schemas/v2/spot'

/** OCR 注意喚起データのうち表示に使う項目（API スキーマ由来） */
export type OcrWarning = Pick<NonNullable<SpotStampData['ocrWarning']>, 'title' | 'content'>

type Props = {
  /** API から取得した OCR 注意喚起データ。指定時は動的 HTML を表示し、未指定時は静的フォールバックを表示 */
  ocrWarning?: OcrWarning | null
}

const hasContent = (content: string | null | undefined): content is string =>
  typeof content === 'string' && content.replace(/<[^>]*>/g, '').trim().length > 0

/**
 * OCR 注意喚起ダイアログの本文（管理画面設定の HTML、未設定時は静的な良い例・悪い例）。
 * 余白・ボタン・チェックボックスは DialogOcrWarning 側で持つ（#3695）。
 */
export const OcrWarningContent = ({ ocrWarning }: Props) => {
  const { t } = useTranslation()

  if (ocrWarning != null && hasContent(ocrWarning.content)) {
    return <HtmlContent html={ocrWarning.content} variant="body2" />
  }

  return (
    <div>
      {/*良い例（先頭見出しは上マージンなし: ヘッダー直下の余白を Dialog 標準に揃える）*/}
      <div>
        <h1 className={'text-sm font-bold text-left text-[#a00]'}>{t('ocr.warning.good_example', '○ 良い例')}</h1>
        <p className={'text-xs mt-3 mb-2'}>{t('ocr.warning.good_intro', '以下の条件をすべて満たしてください。')}</p>
        <ul className={'list-disc pl-4 text-xs'}>
          <li>{t('ocr.warning.good_item_1', 'しわ、破れ、汚れ、丸まり、影などがない')}</li>
          <li>{t('ocr.warning.good_item_2', '手書きの文字がない')}</li>
          <li>{t('ocr.warning.good_item_3', '手で持たず、暗い色の物の上にレシートを置く')}</li>
          <li>{t('ocr.warning.good_item_4', 'レシート全体が写るように撮影する')}</li>
        </ul>
      </div>
      {/*悪い例*/}
      <div>
        <h1 className={'text-sm font-bold mt-3 text-left text-[#a00]'}>{t('ocr.warning.bad_example', '× 悪い例')}</h1>
        <h2 className={'text-xs font-bold text-center'}>{t('ocr.warning.bad_1', '真上から撮影していない。')}</h2>
        <h2 className={'text-xs font-bold text-center'}>
          {t('ocr.warning.bad_2', 'レシートが上下や左右に丸まっている。')}
        </h2>
        <h2 className={'text-xs font-bold text-center'}>{t('ocr.warning.bad_other', 'その他')}</h2>
        <p className={'text-left text-xs'}>
          {t('ocr.warning.bad_intro', '以下の条件に1つでも当てはまるとエラーになります。')}
        </p>
        <ul className={'list-disc pl-4 text-xs'}>
          <li>{t('ocr.warning.bad_item_1', 'しわ、破れ、汚れ、丸まり、影がある')}</li>
          <li>{t('ocr.warning.bad_item_2', '手書きの文字が書かれている')}</li>
          <li>{t('ocr.warning.bad_item_3', 'レシート以外の不要な文字が写っている')}</li>
          <li>{t('ocr.warning.bad_item_4', 'レシートが著しく傾いている、歪んでいる、または一部が写っていない')}</li>
        </ul>
      </div>

      {/* 注意事項: 下側の余白は DialogOcrWarning の gap で取る */}
      <div className="mt-6 p-4 bg-[#ffc] border border-[#ddd]">
        <h1 className={'text-sm mb-3 text-center text-[#a00] font-bold'}>
          {t('ocr.warning.notice_title', '注意事項')}
        </h1>
        <h1 className={'text-xs'}>
          {t(
            'ocr.warning.notice_body',
            'レシートの複製、数字の偽装などの悪質な不正があった場合はアカウント削除などの措置を取らせていただく場合がございますのでご了承ください。',
          )}
        </h1>
      </div>
    </div>
  )
}
